import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import ProductCard from "../components/ProductCard";
import { ChevronRight, ChevronLeft, Car, Wrench } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VehiclePage = () => {
  const { brandSlug, modelSlug, yearRange, partSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [brand, setBrand] = useState(null);
  const [model, setModel] = useState(null);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [engines, setEngines] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);

  const page = parseInt(searchParams.get("page") || "1");
  const sort = searchParams.get("sort") || "price_asc";

  // Fetch categories from DB
  useEffect(() => {
    axios.get(`${API}/vehicles/categories`).then((r) => {
      setCategories(r.data || []);
    }).catch(() => {});
  }, []);

  // Find active category by slug
  useEffect(() => {
    if (partSlug && categories.length > 0) {
      for (const cat of categories) {
        if (cat.slug === partSlug) { setActiveCategory(cat); return; }
        for (const child of (cat.children || [])) {
          if (child.slug === partSlug) { setActiveCategory(child); return; }
        }
      }
      setActiveCategory({ name: partSlug.replace(/-/g, " "), slug: partSlug });
    } else {
      setActiveCategory(null);
    }
  }, [partSlug, categories]);

  useEffect(() => {
    const fetchVehicleData = async () => {
      try {
        if (yearRange && modelSlug) {
          const { data } = await axios.get(`${API}/vehicles/by-slug/${brandSlug}/${modelSlug}/${yearRange}`);
          setBrand(data.brand);
          setModel(data.model);
          setYears(data.year ? [data.year] : []);
          setModels([]);
          if (data.engines) setEngines(data.engines);
        } else if (modelSlug) {
          const { data } = await axios.get(`${API}/vehicles/by-slug/${brandSlug}/${modelSlug}`);
          setBrand(data.brand);
          setModel(data.model);
          setYears(data.years || []);
          setModels([]);
          setEngines(data.engines || []);
        } else if (brandSlug) {
          const { data } = await axios.get(`${API}/vehicles/by-slug/${brandSlug}`);
          setBrand(data.brand);
          setModels(data.models || []);
          setModel(null);
          setYears([]);
          setEngines([]);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchVehicleData();
  }, [brandSlug, modelSlug, yearRange]);

  const fetchProducts = useCallback(async () => {
    if (!brand) return;
    setLoading(true);
    try {
      // If we have a specific year, use product_vehicle_map for exact matching
      const yearObj = years.length === 1 ? years[0] : null;
      if (yearObj && yearObj.id) {
        const params = { page, limit: 24, sort };
        if (partSlug) params.category_slug = partSlug;
        const { data } = await axios.get(`${API}/oem/vehicle-products/${yearObj.id}`, { params });
        setProducts(data.products || []);
        setTotal(data.total || 0);
        setPages(data.pages || Math.ceil((data.total || 0) / 24));
      } else {
        // Fallback to text search for brand/model level
        const searchTerms = [brand.name];
        if (model) searchTerms.push(model.name);
        if (partSlug) {
          const catName = activeCategory?.name || partSlug.replace(/-/g, " ");
          searchTerms.push(catName);
        }
        const params = { page, limit: 24, sort, search: searchTerms.join(" ") };
        const { data } = await axios.get(`${API}/products`, { params });
        setProducts(data.products || []);
        setTotal(data.total || 0);
        setPages(data.pages || Math.ceil((data.total || 0) / 24));
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [brand, model, years, partSlug, activeCategory, page, sort]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const goPage = (pg) => {
    const p = new URLSearchParams(searchParams);
    p.set("page", String(pg));
    setSearchParams(p);
    window.scrollTo(0, 0);
  };

  const brandName = brand?.name || brandSlug || "";
  const modelName = model?.name || "";
  const yearLabel = yearRange ? yearRange.replace("-", " - ") : "";
  const partName = activeCategory?.name || (partSlug ? partSlug.replace(/-/g, " ") : "");
  const vehicleLabel = [brandName, modelName, yearLabel].filter(Boolean).join(" ");
  const fullLabel = partName ? `${vehicleLabel} ${partName}` : vehicleLabel;

  const pageTitle = `${fullLabel} Yedek Parca | Pazarlo`;
  const pageDesc = `${fullLabel} - ${total.toLocaleString("tr-TR")} urun. En uygun fiyatlarla oto yedek parca Pazarlo'da.`;
  const basePath = `/arac/${brandSlug}${modelSlug ? "/" + modelSlug : ""}${yearRange ? "/" + yearRange : ""}`;
  const canonicalUrl = `https://pazarlo.com${basePath}${partSlug ? "/" + partSlug : ""}`;

  const crumbs = [{ name: "Ana Sayfa", url: "/" }];
  if (brandSlug) crumbs.push({ name: brandName, url: `/arac/${brandSlug}` });
  if (modelSlug) crumbs.push({ name: modelName, url: `/arac/${brandSlug}/${modelSlug}` });
  if (yearRange) crumbs.push({ name: yearLabel, url: `/arac/${brandSlug}/${modelSlug}/${yearRange}` });
  if (partSlug) crumbs.push({ name: partName, url: canonicalUrl });

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: pageTitle,
    description: pageDesc,
    url: canonicalUrl,
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: total,
      name: `${fullLabel} Yedek Parca`,
    },
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Pazarlo" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-4" data-testid="vehicle-page">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb">
          <ol className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500 mb-4" itemScope itemType="https://schema.org/BreadcrumbList">
            {crumbs.map((c, i) => (
              <li key={i} className="flex items-center gap-1.5" itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                {i > 0 && <ChevronRight size={12} />}
                {i < crumbs.length - 1 ? (
                  <Link to={c.url} className="hover:text-orange-500" itemProp="item"><span itemProp="name">{c.name}</span></Link>
                ) : (
                  <span className="text-gray-800 font-medium" itemProp="name">{c.name}</span>
                )}
                <meta itemProp="position" content={String(i + 1)} />
              </li>
            ))}
          </ol>
        </nav>

        {/* Title with Car Image */}
        <div className="mb-5">
          <div className="flex items-center gap-4">
            {modelSlug && (
              <img
                src={yearRange ? "/cars/" + brandSlug + "/" + modelSlug + "-" + yearRange.replace("-", "-") + ".png" : "/cars/" + brandSlug + "/" + modelSlug + ".png"}
                alt={vehicleLabel}
                className="w-32 h-20 object-contain hidden sm:block"
                onError={(e) => { e.target.style.display = "none"; }}
                data-testid="vehicle-hero-image"
              />
            )}
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2" data-testid="vehicle-title">
                {partSlug ? <Wrench size={22} className="text-orange-500" /> : <Car size={22} className="text-orange-500" />}
                {fullLabel}
                {!partSlug && " Yedek Parca"}
              </h1>
              <p className="text-sm text-gray-500 mt-1">{total.toLocaleString("tr-TR")} urun bulundu</p>
            </div>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Sidebar */}
          <aside className="w-56 shrink-0 hidden lg:block" data-testid="vehicle-sidebar">
            {/* Engines (when year selected) */}
            {yearRange && engines.length > 0 && (
              <div className="bg-white rounded-lg border p-3 mb-3">
                <h3 className="font-semibold text-sm text-gray-800 mb-2">Motorlar</h3>
                <ul className="space-y-0.5">
                  {engines.map((e) => (
                    <li key={e.id} className="flex items-center justify-between px-2 py-1.5 text-xs text-gray-600">
                      <span className="font-medium">{e.engine_code}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                        e.fuel === "Dizel" ? "bg-gray-100 text-gray-600" :
                        e.fuel === "Hibrit" ? "bg-green-50 text-green-600" :
                        e.fuel === "Elektrik" ? "bg-blue-50 text-blue-600" :
                        e.fuel === "LPG" ? "bg-yellow-50 text-yellow-600" :
                        "bg-red-50 text-red-600"
                      }`}>{e.fuel}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Part categories */}
            {(yearRange || modelSlug) && categories.length > 0 && (
              <div className="bg-white rounded-lg border p-3 mb-3">
                <h3 className="font-semibold text-sm text-gray-800 mb-2 flex items-center gap-1.5">
                  <Wrench size={14} /> Parca Kategorileri
                </h3>
                <ul className="space-y-0.5">
                  {categories.map((cat) => (
                    <li key={cat.slug}>
                      <Link
                        to={`${basePath}/${cat.slug}`}
                        className={`block px-2 py-1.5 rounded text-xs font-medium transition ${
                          partSlug === cat.slug
                            ? "bg-orange-50 text-orange-600"
                            : "text-gray-700 hover:bg-orange-50 hover:text-orange-600"
                        }`}
                      >
                        {cat.name}
                      </Link>
                      {cat.children && cat.children.length > 0 && (
                        partSlug === cat.slug || cat.children.some(c => c.slug === partSlug)
                      ) && (
                        <ul className="ml-3 mt-0.5 space-y-0.5 border-l border-gray-200 pl-2">
                          {cat.children.map((sub) => (
                            <li key={sub.slug}>
                              <Link
                                to={`${basePath}/${sub.slug}`}
                                className={`block px-2 py-1 rounded text-[11px] transition ${
                                  partSlug === sub.slug
                                    ? "bg-orange-50 text-orange-600 font-medium"
                                    : "text-gray-500 hover:text-orange-600"
                                }`}
                              >
                                {sub.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Models (brand only) */}
            {!modelSlug && models.length > 0 && (
              <div className="bg-white rounded-lg border p-3 mb-3">
                <h3 className="font-semibold text-sm text-gray-800 mb-2">{brandName} Modelleri</h3>
                <ul className="space-y-0.5">
                  {models.map((m) => (
                    <li key={m.id}>
                      <Link to={`/arac/${brandSlug}/${m.slug}`}
                        className="flex items-center gap-2 w-full px-2 py-1.5 rounded text-xs text-gray-600 hover:bg-orange-50 hover:text-orange-600 transition"
                      >
                        <img src={"/cars/" + brandSlug + "/" + m.slug + ".png"} alt={m.name} className="w-10 h-7 object-contain" onError={(e) => { e.target.style.display = "none"; }} />
                        <span className="flex-1">{m.name}</span>
                        <span className="text-[10px] text-gray-400">{m.body_type}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Years (model selected, no year) */}
            {modelSlug && !yearRange && years.length > 0 && (
              <div className="bg-white rounded-lg border p-3 mb-3">
                <h3 className="font-semibold text-sm text-gray-800 mb-2">{modelName} Yillari</h3>
                <ul className="space-y-0.5">
                  {years.map((y) => (
                    <li key={y.id}>
                      <Link to={`/arac/${brandSlug}/${modelSlug}/${y.year_start}-${y.year_end}`}
                        className="flex items-center gap-2 px-2 py-1.5 rounded text-xs text-gray-600 hover:bg-orange-50 hover:text-orange-600 transition"
                      >
                        <img src={`/cars/${brandSlug}/${modelSlug}-${y.year_start}-${y.year_end}.png`} alt="" className="w-12 h-8 object-contain flex-shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
                        {y.year_start} - {y.year_end}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Back links */}
            {partSlug && (
              <Link to={basePath} className="flex items-center gap-1 text-xs text-orange-500 hover:underline mb-2">
                <ChevronLeft size={12} /> Tum Parca Tipleri
              </Link>
            )}
            {yearRange && !partSlug && (
              <Link to={`/arac/${brandSlug}/${modelSlug}`} className="flex items-center gap-1 text-xs text-orange-500 hover:underline mb-2">
                <ChevronLeft size={12} /> Tum {modelName} Yillari
              </Link>
            )}
            {modelSlug && !yearRange && (
              <Link to={`/arac/${brandSlug}`} className="flex items-center gap-1 text-xs text-orange-500 hover:underline mb-2">
                <ChevronLeft size={12} /> Tum {brandName} Modelleri
              </Link>
            )}
            {brandSlug && !modelSlug && (
              <Link to="/products" className="flex items-center gap-1 text-xs text-orange-500 hover:underline mb-2">
                <ChevronLeft size={12} /> Tum Urunler
              </Link>
            )}
          </aside>

          {/* Main */}
          <main className="flex-1 min-w-0">
            {/* Part type pills (mobile) */}
            {(yearRange || modelSlug) && categories.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-4 lg:hidden">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`${basePath}/${cat.slug}`}
                    className={`px-2.5 py-1 rounded-full text-[11px] border transition ${
                      partSlug === cat.slug
                        ? "bg-orange-500 text-white border-orange-500"
                        : "bg-white text-gray-600 border-gray-200 hover:border-orange-300"
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            )}

            {/* Sort */}
            <div className="flex items-center justify-end mb-4">
              <select
                value={sort}
                onChange={(e) => {
                  const p = new URLSearchParams(searchParams);
                  p.set("sort", e.target.value);
                  p.set("page", "1");
                  setSearchParams(p);
                }}
                className="text-xs border rounded-md px-3 py-1.5 bg-white"
              >
                <option value="price_asc">Fiyat (Dusukten Yuksege)</option>
                <option value="price_desc">Fiyat (Yuksekten Dusuge)</option>
                <option value="name_asc">Isim (A-Z)</option>
                <option value="newest">En Yeniler</option>
              </select>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-3 border-orange-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20 text-gray-500">
                <p>Bu arac icin urun bulunamadi</p>
                {partSlug && (
                  <Link to={basePath} className="text-orange-500 hover:underline text-sm mt-2 inline-block">
                    Tum {vehicleLabel} urunlerini goster
                  </Link>
                )}
                {!partSlug && modelSlug && (
                  <Link to={`/arac/${brandSlug}`} className="text-orange-500 hover:underline text-sm mt-2 inline-block">
                    Tum {brandName} urunlerini goster
                  </Link>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {products.map((p) => (
                    <ProductCard key={p.sku || p.id} product={p} />
                  ))}
                </div>

                {pages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-6">
                    <button onClick={() => goPage(Math.max(1, page - 1))} disabled={page <= 1} className="p-2 rounded-md border bg-white disabled:opacity-30">
                      <ChevronLeft size={16} />
                    </button>
                    {Array.from({ length: Math.min(pages, 7) }, (_, i) => {
                      let pg;
                      if (pages <= 7) pg = i + 1;
                      else if (page <= 4) pg = i + 1;
                      else if (page >= pages - 3) pg = pages - 6 + i;
                      else pg = page - 3 + i;
                      return (
                        <button key={pg} onClick={() => goPage(pg)}
                          className={`w-8 h-8 rounded-md text-xs font-medium transition ${pg === page ? "bg-orange-500 text-white" : "border bg-white text-gray-600 hover:bg-gray-50"}`}
                        >{pg}</button>
                      );
                    })}
                    <button onClick={() => goPage(Math.min(pages, page + 1))} disabled={page >= pages} className="p-2 rounded-md border bg-white disabled:opacity-30">
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </>
  );
};

export default VehiclePage;
